import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  DsAiRead,
  getReconBillUploadList,
} from "../apis/reconBillUploadService";
import { ReconBillUploadRow } from "../components/patient/BillAuditStage/ReconBillUploadTable";
import { RootState } from "../store/store";
import { errorMessageModal } from "../utils/modalMessage";

const isBillAuditCompletedStage = (visitStages: any[] | undefined) => {
  const billAudit = visitStages?.find(
    (visit) => visit?.visitStage?.toUpperCase() === "BILL AUDIT",
  );
  if (!billAudit) {
    return false;
  }
  const state = String(billAudit?.state || "").trim().toUpperCase();
  return (
    !!billAudit?.completed ||
    state === "SUMMARY GENERATED" ||
    state === "AUDITED"
  );
};

export const useReconBillUploadList = (enabled = true) => {
  const { patients, preAuth } = useSelector((state: RootState) => state);

  const hospitalId = patients?.fetchBasicDetails?.casesResponse?.healthCareId;
  const schemeId = patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId;
  const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
  const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
  const billMappingId =
    patients?.fetchBasicDetails?.visitInfoResponse?.billMappingId;
  const g7CrNo = patients?.fetchBasicDetails?.visitInfoResponse?.creditNumber;
  const visitType = patients?.fetchBasicDetails?.visitInfoResponse?.visitType;

  const skipRegisterMandate = useMemo(
    () => isBillAuditCompletedStage(preAuth?.stepperData?.visitStages),
    [preAuth?.stepperData?.visitStages],
  );

  const [rows, setRows] = useState<ReconBillUploadRow[]>([]);
  const [dsRead, setDsRead] = useState<DsAiRead | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);
  const [isBillAuditBlocked, setIsBillAuditBlocked] = useState(false);
  const [billAuditBlockMessage, setBillAuditBlockMessage] = useState("");

  const fetchRows = useCallback(async (options?: { silent?: boolean }) => {
    if (!hospitalId || !g7CrNo) {
      setRows([]);
      setDsRead(null);
      setIsBillAuditBlocked(false);
      setBillAuditBlockMessage("");
      setHasChecked(true);
      return;
    }

    if (!options?.silent) {
      setLoading(true);
    }
    try {
      const response = await getReconBillUploadList({
        hospitalId,
        schemeId,
        g7CrNo,
        billMappingId,
        visitType,
        caseId,
        visitId,
      });
      if (response?.status === false) {
        const message =
          response?.message ||
          "Unable to load recon bill upload list. Please check register data.";
        // Audited / Summary Generated: do not apply register-mandate block.
        if (!skipRegisterMandate) {
          errorMessageModal(message);
          setIsBillAuditBlocked(true);
          setBillAuditBlockMessage(message);
        } else {
          setIsBillAuditBlocked(false);
          setBillAuditBlockMessage("");
        }
        setRows([]);
        setDsRead(null);
        return;
      }
      setIsBillAuditBlocked(false);
      setBillAuditBlockMessage("");
      const items = Array.isArray(response?.data)
        ? response.data
        : response?.data?.items || [];
      setRows(
        items.map((item: ReconBillUploadRow) => ({
          ...item,
          key: item.reconId,
          documentKind: item.documentKind || "BILL",
        })),
      );
      setDsRead(
        Array.isArray(response?.data) ? null : response?.data?.dsRead || null,
      );
    } catch (err) {
      console.error("Failed to fetch recon bill upload list", err);
      setRows([]);
      setDsRead(null);
      setIsBillAuditBlocked(false);
      setBillAuditBlockMessage("");
    } finally {
      if (!options?.silent) {
        setLoading(false);
      }
      setHasChecked(true);
    }
  }, [
    hospitalId,
    schemeId,
    g7CrNo,
    billMappingId,
    visitType,
    caseId,
    visitId,
    skipRegisterMandate,
  ]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    setHasChecked(false);
    fetchRows();
  }, [enabled, fetchRows]);

  // If stage moves to Audited / Summary Generated, clear any prior block.
  useEffect(() => {
    if (skipRegisterMandate) {
      setIsBillAuditBlocked(false);
      setBillAuditBlockMessage("");
    }
  }, [skipRegisterMandate]);

  const hasReconBillRows = hasChecked && rows.length > 0;

  return {
    rows,
    dsRead,
    loading,
    hasChecked,
    hasReconBillRows,
    isBillAuditBlocked: skipRegisterMandate ? false : isBillAuditBlocked,
    billAuditBlockMessage: skipRegisterMandate ? "" : billAuditBlockMessage,
    refetch: fetchRows,
    hospitalId,
    schemeId,
    billMappingId,
    g7CrNo,
  };
};
